import React, { useEffect, useState } from "react";
import { MdLocalShipping, MdLock } from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import {
  addRunOrders,
  fetchRuns,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import { leftOutReason } from "./CreateRunModal";
import RunDialog from "./RunDialog";
import SheetChip from "./SheetChip";
import { sortRun } from "./runOrder";
import {
  driverName,
  isLockedRun,
  isOpenRun,
  lockedByText,
  RUN_MAX_STOPS,
  runDay,
  runTitle,
  stopsText,
} from "./runs";

/**
 * Add to run: adds the ticked orders to an open run and sorts its stops for
 * the shortest drive again. `orders` are the ticked rows; ones already on an
 * open run, fulfilled or cancelled are left out with a note. A locked run is
 * listed as locked and can't be picked. `locations` are the account's
 * locations (where runs start). `onAdded(run, sorting)` gets the run as saved
 * and sortRun's result ({ sorted, problem }).
 */
const AddToRunModal = ({
  open,
  orders = [],
  locations = [],
  onAdded,
  onClose,
}) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [runId, setRunId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setRunId(null);
    setError("");
    fetchRuns().then((response) => {
      if (cancelled) return;
      const list = response?.data?.result?.data;
      setFailed(Boolean(response.error) || !Array.isArray(list));
      setRuns(Array.isArray(list) ? list.filter(isOpenRun) : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const leftOut = orders
    .map((order) => ({ order, reason: leftOutReason(order) }))
    .filter((entry) => entry.reason);
  const adding = orders.filter((order) => !leftOutReason(order));
  const chosen = runs.find((run) => run.id === runId && !isLockedRun(run));
  const overLimit =
    chosen && Number(chosen.stops_count ?? 0) + adding.length > RUN_MAX_STOPS;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await addRunOrders(
        runId,
        adding.map((order) => order.id),
      );
      if (response.error) {
        setError(
          response.error?.response?.data?.message ||
            "Couldn't add the orders. Try again.",
        );
        return;
      }
      const saved = response?.data?.result;
      const sorting =
        saved?.id != null
          ? await sortRun(saved, locations)
          : { run: saved, sorted: false };
      onAdded(sorting.run, sorting);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RunDialog
      open={open}
      labelId="addToRunTitle"
      title="Add to run"
      subtitle={`${adding.length} order${adding.length === 1 ? " joins" : "s join"} the run, then its stops are sorted for the shortest drive`}
      icon={MdLocalShipping}
      widthClass="max-w-[560px]"
      closeDisabled={saving}
      onClose={onClose}
      footer={
        <>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Cancel"
            onClick={onClose}
            disabled={saving}
          />
          <BoltButton
            type="button"
            variant="primary"
            size="sm"
            label={saving ? "Adding…" : "Add to run"}
            onClick={save}
            disabled={saving || !chosen || !adding.length || overLimit}
          />
        </>
      }
    >
      {leftOut.length > 0 && (
        <div className="grid gap-[4px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg px-[12px] py-[8px] text-[12px] text-state-warning-text">
          {leftOut.map(({ order, reason }) => (
            <span key={order.id}>
              #{order.id} is left out: {reason}.
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid min-h-[120px] place-items-center">
          <CircularLoader size={24} />
        </div>
      ) : failed ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[10px] text-[12px] text-state-danger-text">
          Couldn't load the runs. Close and try again.
        </p>
      ) : runs.length === 0 ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[12px] py-[14px] text-[12px] text-ui-text-muted">
          No open runs. Use Create run instead.
        </p>
      ) : (
        <ul
          role="radiogroup"
          aria-label="Open runs"
          className="m-[0px] list-none overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[0px]"
        >
          {runs.map((run) => {
            const locked = isLockedRun(run);
            return (
              <li
                key={run.id}
                className="border-b-[1px] border-ui-border-subtle last:border-b-0"
              >
                <label
                  className={`flex min-h-[44px] items-center gap-[10px] px-[12px] py-[8px] ${
                    locked
                      ? "cursor-not-allowed"
                      : "cursor-pointer hover:bg-ui-surface-sunken"
                  }`}
                >
                  <input
                    type="radio"
                    name="addToRun"
                    checked={runId === run.id}
                    disabled={locked}
                    onChange={() => setRunId(run.id)}
                    className="h-[16px] w-[16px] shrink-0 accent-brand disabled:opacity-40"
                  />
                  <div className="grid min-w-[0px] flex-1 gap-[1px]">
                    <span
                      className={`truncate font-semibold ${locked ? "text-ui-text-muted" : ""}`}
                    >
                      {runTitle(run)}
                    </span>
                    <span className="truncate text-ui-text-muted">
                      {[
                        runDay(run.run_date),
                        driverName(run.driver) || "No driver",
                        stopsText(run.stops_count),
                      ].join(" · ")}
                    </span>
                    {locked && (
                      <span className="truncate text-[11px] text-state-warning-text">
                        {lockedByText(run)}. Unlock it to add orders.
                      </span>
                    )}
                  </div>
                  {locked && (
                    <SheetChip tone="warning">
                      <MdLock
                        aria-hidden="true"
                        className="h-[11px] w-[11px]"
                      />
                      Locked
                    </SheetChip>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {overLimit && (
        <p className="m-[0px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg px-[12px] py-[8px] text-[12px] text-state-warning-text">
          A run takes up to {RUN_MAX_STOPS} orders. This one would have{" "}
          {Number(chosen.stops_count ?? 0) + adding.length}.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text"
        >
          {error}
        </p>
      )}
    </RunDialog>
  );
};

export default AddToRunModal;
